import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DateRange } from "../../components/Core/AppDateTimePicker/AppDateTimePicker";
import AppRangePicker from "../../components/Core/AppRangePicker/AppRangePicker";
import AppText from "../../components/Core/AppText/AppText";
import Flex from "../../components/Layout/Flex";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { EventType, Schedule, ScheduleRow } from "../../components/Shared/Schedule/Schedule";
import { APPLICATION_STATUS } from "../../configs/constants/ApplicationStatus";
import Consultant from "../../models/Consultant";
import Job, { ApplicationWithExpansion, Shift } from "../../models/Job";
import { useConsultantAbsence, useConsultantApplications } from "../../networking/consultant.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { getServiceName } from "../../utils/competence.utils";
import { dateRangeToDays } from "../../utils/date.time.utils";
import { customerRoute, jobRoute } from "../../utils/route.utils";

type ApplicationWithJobAndShifts = ApplicationWithExpansion<{ job: Job }>;

const ConsultantShiftsTab: React.FC<{ consultant: Consultant }> = ({ consultant }) => {
	const { data: organisation } = useOrganisation();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { absences } = useConsultantAbsence({
		consultantId: consultant.id,
	});

	// Note: Make use of this when adding range filters etc
	const [selectedDateRange, setSelectedDateRange] = useState({
		startDate: dayjs().startOf("week").subtract(2, "week").toDate(),
		endDate: dayjs().add(4, "week").endOf("week").toDate(),
	});

	const { data: applications, isLoading } = useConsultantApplications<ApplicationWithJobAndShifts>({
		consultantId: consultant.id,
		expand: ["job", "job.customer", "shifts"],
		page: 1,
		pageSize: 2000,
		status: [APPLICATION_STATUS.ACCEPTED, APPLICATION_STATUS.PENDING_ANSWER],
		startDate: selectedDateRange.startDate.toISOString(),
		endDate: selectedDateRange.endDate.toISOString(),
		includeOverlappingRange: true,
	});

	const rows = useMemo(() => {
		if (!applications || !applications.length) return [];

		const absenceDays = (absences || []).flatMap((absence) =>
			dateRangeToDays(absence.startDate!, absence.endDate!).map((d) => ({
				...d,
				absenceId: absence.id!,
				absenceType: absence.type!,
			}))
		);

		const absenceRow: ScheduleRow = {
			id: consultant.id + "_absence",
			title: (
				<AppText textAlign="right" weight="medium" cursor="pointer" size="s">
					{t("ABSENCE")}
				</AppText>
			),
			onClick: () => {},
			events:
				absenceDays.map((item) => ({
					id: item.absenceId,
					start: item.start,
					end: item.end,
					type: "absence" as EventType,
				})) || [],
		};

		const groupedApplicationByJob = applications.reduce<
			Record<string, ApplicationWithJobAndShifts[]>
		>((acc, curr) => {
			// Consultant may have multiple applications for the same job
			if (acc[curr.job.id]) {
				acc[curr.job.id].push(curr);
			} else {
				acc[curr.job.id] = [curr];
			}

			return acc;
		}, {});

		const jobRows = Object.keys(groupedApplicationByJob).map<ScheduleRow>((jobId) => {
			const applications = groupedApplicationByJob[jobId];

			const job = applications[0].job;

			const shiftsWithApplicationStatus = applications.reduce<
				(Shift & { applicationStatus: APPLICATION_STATUS })[]
			>((acc, curr) => {
				for (const shift of curr.shifts) {
					const existingEntry = acc.find((s) => s.id === shift.id);

					if (existingEntry && existingEntry.applicationStatus !== APPLICATION_STATUS.ACCEPTED) {
						// Consultant may have multiple applications for the same shift but only one can be accepted
						// so favor accepted over pending
						existingEntry.applicationStatus = curr.status;
					} else if (!existingEntry) {
						acc.push({ ...shift, applicationStatus: curr.status });
					}
				}

				return acc;
			}, []);

			return {
				id: jobId,
				title: (
					<>
						<AppText
							textAlign="right"
							weight="medium"
							cursor="pointer"
							size="s"
							onClick={() => navigate(customerRoute(job.customer.id))}
						>
							{job.customer.name}
						</AppText>
						<AppText
							textAlign="right"
							cursor="pointer"
							color="grayText"
							onClick={() => navigate(jobRoute(job.id))}
						>
							<>
								#{job.orderNumber},{" "}
								{getServiceName(organisation, job.competence.branch, job.competence.service)}
							</>
						</AppText>
					</>
				),
				onClick: () => {},
				events: shiftsWithApplicationStatus.map(({ applicationStatus, ...shift }) => ({
					...shift,
					id: shift.id!,
					type: applicationStatus === APPLICATION_STATUS.ACCEPTED ? "shift-booked" : "shift-open",
					job,
					shift,
				})),
			};
		});

		if (absenceRow.events.length > 0) {
			return [absenceRow, ...jobRows];
		}

		return jobRows;
	}, [applications, consultant, absences]);

	return (
		<div>
			<Flex justifyContent="flex-end">
				<AppRangePicker
					justifyRight
					placeholder={t("timeReports.selectDateRange")}
					value={
						selectedDateRange
							? {
									start: selectedDateRange.startDate,
									end: selectedDateRange.endDate,
							  }
							: undefined
					}
					onChange={(range?: DateRange) =>
						range &&
						setSelectedDateRange({
							startDate: range.start,
							endDate: range.end,
						})
					}
				/>
			</Flex>
			{isLoading ? (
				<LoadingIndicator />
			) : rows.length === 0 ? (
				<EmptyState label="Det finns inga uppdrag" />
			) : (
				<Schedule rows={rows} range={selectedDateRange} />
			)}
		</div>
	);
};

export default ConsultantShiftsTab;
